import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { content } from "../i18n/content";

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yA = useTransform(scrollYProgress, [0, 1], ["-4%", "4%"]);
  const yB = useTransform(scrollYProgress, [0, 1], ["4%", "-4%"]);

  return (
    <section id="about" ref={ref} className="relative py-28 sm:py-40 overflow-hidden" data-testid="about-section">
      <div className="max-w-[1500px] mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-12 items-start">
        <div className="lg:col-span-6 grid grid-cols-2 gap-5 sm:gap-8 items-start">
          <motion.figure
            style={{ y: yA }}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="bg-[#f2ebe1] p-3 border border-[#2a2626]/10"
          >
            <img
              src="/lesson-presenting.jpg"
              alt={content.about.portraitCaption}
              className="w-full h-auto block"
              data-testid="teaching-image"
            />
            <figcaption className="pt-3 pb-1 text-center text-[9px] sm:text-[10px] uppercase tracking-[0.24em] text-[#5c5656]" data-testid="portrait-caption">
              {content.about.portraitCaption}
            </figcaption>
          </motion.figure>

          <motion.figure
            style={{ y: yB }}
            initial={{ opacity: 0, y: 70 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="bg-[#f2ebe1] p-3 border border-[#2a2626]/10 mt-10 sm:mt-20"
          >
            <img
              src="/library-study.jpg"
              alt={content.about.libraryCaption}
              className="w-full h-auto block"
              data-testid="library-image"
            />
            <figcaption className="pt-3 pb-1 text-center text-[9px] sm:text-[10px] uppercase tracking-[0.24em] text-[#5c5656]" data-testid="library-caption">
              {content.about.libraryCaption}
            </figcaption>
          </motion.figure>
        </div>

        <div className="lg:col-span-5 lg:col-start-8">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-[11px] uppercase tracking-[0.35em] text-[#c86f53] mb-8"
            data-testid="about-chapter-label"
          >
            {content.about.chapter}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[#2a2626] font-medium mb-4"
            data-testid="about-name"
          >
            {content.about.name}
          </motion.h2>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="mb-10"
            data-testid="about-role"
          >
            <p className="font-display italic text-xl sm:text-2xl text-[#6b705c]">{content.about.roleDe}</p>
            <p className="font-display italic text-lg text-[#6b705c]/70">{content.about.roleEn}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.3 }}
            className="space-y-7 max-w-xl"
            data-testid="about-text"
          >
            {content.about.paragraphs.map((p, i) => (
              <div key={i} className={i > 0 ? "border-t border-[#2a2626]/10 pt-6" : ""}>
                <p className="text-sm sm:text-base leading-relaxed text-[#2a2626]/85 font-light">{p.de}</p>
                <p className="mt-2 font-display italic text-base sm:text-lg leading-relaxed text-[#6b705c] font-light">
                  {p.en}
                </p>
              </div>
            ))}
          </motion.div>

          <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl" data-testid="about-facts">
            {content.about.facts.map((f, i) => (
              <motion.div
                key={f.k}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.4 + i * 0.12 }}
                className="border-t border-[#2a2626]/15 pt-4"
                data-testid={`about-fact-${i}`}
              >
                <p className="font-display text-3xl sm:text-4xl text-[#c86f53] font-medium">{f.k}</p>
                <p className="mt-1 text-[10px] sm:text-[11px] uppercase tracking-[0.16em] text-[#5c5656]">{f.de}</p>
                <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.16em] text-[#5c5656]/60">{f.en}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
